import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import path from "path-browserify";
import { useState } from "react";
import { useMutation } from "urql";

import { useUrqlResponseHandler } from "../../../hooks";
import { useDrive } from "../DriveContext";
import { CreateDirectory } from "../queries.graphql";

type DriveNewDirectoryDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
};

export const DriveNewDirectoryDialog = ({
  isOpen,
  onClose,
  workspaceId,
}: DriveNewDirectoryDialogProps) => {
  const { currentDirectoryPath, setCurrentDirectoryPath } = useDrive();
  const [newDirectoryName, setNewDirectoryName] = useState("");
  const { getResponseFromState } = useUrqlResponseHandler();
  const [, createDirectory] = useMutation(CreateDirectory);

  const handleDialogClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    const newPath = path.join(currentDirectoryPath, newDirectoryName);
    void createDirectory({
      createDirectoryInput: {
        workspaceId,
        name: newPath,
      },
    }).then((result) => {
      const { error } = getResponseFromState({
        state: result,
        errorMessage: "Failed to create directory",
      });
      if (!error) {
        onClose();
        setNewDirectoryName("");
        setCurrentDirectoryPath?.(newPath);
      }
    });
  };

  return (
    <Dialog
      data-testid="new-directory-dialog"
      open={isOpen}
      onClose={onClose}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "400px",
          },
        },
      }}
    >
      <DialogTitle>New Directory</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          data-testid="directory-name-input"
          label="Directory Name"
          type="text"
          value={newDirectoryName}
          onChange={(event) => setNewDirectoryName(event.target.value)}
          onKeyDown={(event) => {
            switch (event.key) {
              case "Enter":
                handleSubmit();
                break;
              case "Escape":
                handleDialogClose();
                break;
            }
          }}
          fullWidth
          variant="standard"
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleDialogClose}
          data-testid="cancel-btn"
          variant="contained"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          data-testid="submit-btn"
          variant="contained"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
